import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  initializeLeadTracking,
  getStoredLeadSource,
} from "../utils/leadTracking";
import axios from "../utils/axios";

/**
 * Component to handle referral links
 * URL format: /ref/REFCODE or /referral?ref=REFCODE
 */
const ReferralHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [referralInfo, setReferralInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const processReferral = async () => {
      // Get referral code from URL parameters
      const referralCode =
        searchParams.get("ref") || searchParams.get("referral");

      if (!referralCode) {
        setError("No referral code provided");
        setTimeout(() => navigate("/register"), 2000);
        return;
      }

      try {
        // Initialize tracking to capture referral data
        initializeLeadTracking();

        // Try to fetch referral information
        const response = await axios.get(
          `/api/referrals/validate/${referralCode}`
        );
        setReferralInfo(response.data);

        // Add a small delay for user experience
        setTimeout(() => {
          setIsProcessing(false);
          // Redirect to registration with referral context
          navigate("/register?ref=" + referralCode, { replace: true });
        }, 2000);
      } catch (error) {
        console.warn("Referral validation failed:", error);
        // Even if referral validation fails, we still want to track it
        setTimeout(() => {
          setIsProcessing(false);
          navigate("/register?ref=" + referralCode, { replace: true });
        }, 2000);
      }
    };

    processReferral();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Invalid Referral Link
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">
            Redirecting to registration page...
          </p>
        </div>
      </div>
    );
  }

  if (isProcessing) {
    const referralCode =
      searchParams.get("ref") || searchParams.get("referral");

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <div className="text-6xl mb-4">🎁</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Welcome from a Friend!
            </h2>
            <p className="text-gray-600">
              You've been referred with code:
              <span className="font-mono text-green-600 ml-1">
                {referralCode}
              </span>
            </p>
            {referralInfo && (
              <div className="mt-4 p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-700">
                  <strong>Referrer:</strong> {referralInfo.referrerName}
                </p>
                {referralInfo.bonus && (
                  <p className="text-sm text-green-700">
                    <strong>Special Bonus:</strong> {referralInfo.bonus}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-center space-x-2 text-gray-500">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
            <span>Applying referral benefits...</span>
          </div>

          <div className="mt-6 space-y-2 text-sm text-gray-600">
            <div className="flex items-center justify-center space-x-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              <span>Validating referral code</span>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              <span>Applying special discounts</span>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
              <span>Redirecting to registration...</span>
            </div>
          </div>

          <div className="mt-6 text-xs text-gray-500">
            <p>🎉 Referral bonuses apply to both you and your friend!</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ReferralHandler;
